import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Package, Mail, Star, Youtube, Facebook, Twitch, Instagram, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import MediaKitNav from "@/components/MediaKitNav";
import AnimatedCounter from "@/components/AnimatedCounter";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function MediaKitLicenciamientoPage() {
  const [stats, setStats] = useState(null);
  const [licensedProducts, setLicensedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Products Carousel state
  const productsCarouselRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, productsRes] = await Promise.all([
        axios.get(`${API}/media-kit/stats`),
        axios.get(`${API}/media-kit/licensed-products`).catch(() => ({ data: { products: [] } }))
      ]);
      setStats(statsRes.data);
      setLicensedProducts(productsRes.data.products || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Products carousel scroll function
  const scrollProductsCarousel = (direction) => {
    if (productsCarouselRef.current) {
      const scrollAmount = 320;
      const currentScroll = productsCarouselRef.current.scrollLeft;
      productsCarouselRef.current.scrollTo({
        left: direction === 'left' ? currentScroll - scrollAmount : currentScroll + scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <img 
          src="https://customer-assets-0z36b82j.emergentagent.net/job_6374765b-0927-4fa7-ace6-9a95eb25e587/artifacts/6cbu7wu9_LOADING.gif" 
          alt="Cargando..."
          className="w-48 h-auto"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col text-white">
      <Navbar />
      
      {/* ===== HEADER SECTION - White with opacity ===== */}
      <section className="relative pt-44 pb-12">
        <div className="absolute inset-0 bg-white/40" />
        
        <div className="max-w-6xl mx-auto px-4 relative z-10">
          {/* Title */}
          <div className="text-center mb-8">
            <h1 
              className="text-5xl md:text-7xl font-bold mb-4 text-black"
              style={{ 
                fontFamily: "'Supersonic Rocketship', Impact, sans-serif",
                WebkitTextStroke: '8px white',
                paintOrder: 'stroke fill'
              }}
            >
              LICENCIAMIENTO
            </h1>
            <p 
              className="text-lg md:text-xl text-[#FFE500]"
              style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
            >
              Lleva a Darkar y sus amigos a tus productos
            </p>
          </div>

          {/* Navigation */}
          <MediaKitNav />
        </div>
      </section>

      {/* ===== STATS SECTION - Black with opacity ===== */}
      <section className="relative py-16">
        <div className="absolute inset-0 bg-black/70" />
        
        <div className="max-w-6xl mx-auto px-4 relative z-10">
          {/* Main Stats - Big Numbers */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-12">
            {/* YouTube */}
            <div className="text-center" data-testid="stat-youtube">
              <Youtube className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-3 text-red-500" />
              <div 
                className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-2"
                style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
              >
                <AnimatedCounter value={stats?.youtube?.subscribers || 0} />
              </div>
              <p className="text-gray-400 text-sm md:text-base uppercase tracking-wide">Suscriptores</p>
            </div>

            {/* Facebook */}
            <div className="text-center" data-testid="stat-facebook">
              <Facebook className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-3 text-blue-500" />
              <div 
                className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-2"
                style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
              >
                <AnimatedCounter value={stats?.facebook?.followers || 0} />
              </div>
              <p className="text-gray-400 text-sm md:text-base uppercase tracking-wide">Seguidores</p>
            </div>

            {/* Twitch */}
            <div className="text-center" data-testid="stat-twitch">
              <Twitch className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-3 text-purple-500" />
              <div 
                className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-2"
                style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
              >
                <AnimatedCounter value={stats?.twitch?.followers || 0} />
              </div>
              <p className="text-gray-400 text-sm md:text-base uppercase tracking-wide">Seguidores</p>
            </div>

            {/* Instagram */}
            <div className="text-center" data-testid="stat-instagram">
              <Instagram className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-3 text-pink-500" />
              <div 
                className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-2"
                style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
              >
                <AnimatedCounter value={stats?.instagram?.followers || 0} />
              </div>
              <p className="text-gray-400 text-sm md:text-base uppercase tracking-wide">Seguidores</p>
            </div>
          </div>

          {/* Total Views Highlight */}
          <div className="text-center pt-8 border-t border-white/20">
            <div className="inline-block" data-testid="stat-total-views">
              <div 
                className="text-5xl md:text-6xl lg:text-7xl font-black text-[#FFE500]"
                style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
              >
                +<AnimatedCounter value={stats?.youtube?.total_views || 0} duration={3000} format="full" />
              </div>
              <p className="text-gray-300 text-lg md:text-xl uppercase tracking-wide">Reproducciones Totales</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== BRANDS SECTION - White with opacity ===== */}
      <section className="relative py-16">
        <div className="absolute inset-0 bg-white/40" />
        
        <div className="max-w-6xl mx-auto px-4 relative z-10">
          <h2 
            className="text-3xl md:text-4xl font-bold text-center mb-12 text-black"
            style={{ 
              fontFamily: "'Supersonic Rocketship', Impact, sans-serif",
              WebkitTextStroke: '6px white',
              paintOrder: 'stroke fill'
            }}
          >
            NUESTRAS MARCAS
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
            {/* Vete a la Versh */}
            <div 
              className="bg-black rounded-2xl overflow-hidden hand-drawn-subtle relative aspect-square max-w-[400px] mx-auto w-full"
              style={{ borderWidth: '4px', borderColor: '#fff', borderStyle: 'solid' }}
              data-testid="brand-versh"
            >
              {/* Brand Image */}
              <img 
                src="https://hosting.photobucket.com/b42fd48f-f097-40a4-90b1-1ef96c48c9fa/e4af3b87-5a46-4243-9e63-83cbba5e4356.png"
                alt="Vete a la Versh"
                className="w-full h-full object-cover"
              />
              {/* Info overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-black/70 p-4 text-center">
                <h3 
                  className="text-xl font-bold mb-2 text-[#E91E8C]"
                  style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
                >
                  VETE A LA VERSH
                </h3>
                <p className="text-gray-200 text-xs leading-relaxed">
                  Comedia animada irreverente y agresiva con personajes coloridos. Dirigida a adultos jóvenes.
                </p>
              </div>
            </div>

            {/* Darkar & Friends */}
            <div 
              className="bg-black rounded-2xl overflow-hidden hand-drawn-subtle relative aspect-square max-w-[400px] mx-auto w-full"
              style={{ borderWidth: '4px', borderColor: '#fff', borderStyle: 'solid' }}
              data-testid="brand-darkar-friends"
            >
              {/* Brand Image */}
              <img 
                src="https://hosting.photobucket.com/b42fd48f-f097-40a4-90b1-1ef96c48c9fa/3ece3679-080b-44c2-b70a-aa87d210a9f1.png"
                alt="Darkar & Friends"
                className="w-full h-full object-cover"
              />
              {/* Info overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-black/70 p-4 text-center">
                <h3 
                  className="text-xl font-bold mb-2 text-[#FFE500]"
                  style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
                >
                  DARKAR & FRIENDS
                </h3>
                <p className="text-gray-200 text-xs leading-relaxed">
                  Diseños de nuestra serie más conocida en un formato más amistoso y con un identificador amigable para consumidor y retail.
                </p>
              </div>
            </div>

            {/* Pandasaurio */}
            <div 
              className="bg-black rounded-2xl overflow-hidden hand-drawn-subtle relative aspect-square max-w-[400px] mx-auto w-full"
              style={{ borderWidth: '4px', borderColor: '#fff', borderStyle: 'solid' }}
              data-testid="brand-pandasaurio"
            >
              {/* Brand Image */}
              <img 
                src="https://hosting.photobucket.com/b42fd48f-f097-40a4-90b1-1ef96c48c9fa/0c0cd768-e25c-4b44-9167-1222c9f7cbfc.png"
                alt="Pandasaurio"
                className="w-full h-full object-cover"
              />
              {/* Info overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-black/70 p-4 text-center">
                <h3 
                  className="text-xl font-bold mb-2 text-[#9146FF]"
                  style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
                >
                  PANDASAURIO
                </h3>
                <p className="text-gray-200 text-xs leading-relaxed">
                  Humor irreverente pero tierno, dirigido a adultos jóvenes con diseños muy amistosos.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== LICENSED PRODUCTS CAROUSEL - Black with opacity ===== */}
      {licensedProducts.length > 0 && (
        <section className="relative py-16">
          <div className="absolute inset-0 bg-black/70" />
          
          <div className="relative z-10">
            <h2 
              className="text-3xl md:text-4xl font-bold text-center mb-12 px-4"
              style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
            >
              <span className="text-[#FFE500]">PRODUCTOS</span>{" "}
              <span className="text-white">LICENCIADOS</span>
            </h2>

            {/* Carousel Container */}
            <div className="relative px-4 md:px-12">
              {/* Navigation Buttons */}
              <button
                onClick={() => scrollProductsCarousel('left')}
                className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 bg-[#FFE500] hover:bg-[#FFE500]/80 text-black rounded-full p-2 md:p-3 transition-all shadow-lg"
                data-testid="products-carousel-prev"
              >
                <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
              </button>
              <button
                onClick={() => scrollProductsCarousel('right')}
                className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 bg-[#FFE500] hover:bg-[#FFE500]/80 text-black rounded-full p-2 md:p-3 transition-all shadow-lg"
                data-testid="products-carousel-next"
              >
                <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
              </button>

              {/* Carousel Track */}
              <div 
                ref={productsCarouselRef}
                className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth px-8 md:px-16 pb-4"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {licensedProducts.map((product, index) => (
                  <div 
                    key={product.id || index}
                    className="flex-shrink-0 w-[280px] md:w-[320px] bg-white/5 border border-white/10 rounded-2xl overflow-hidden"
                    data-testid={`licensed-product-${index}`}
                  >
                    {/* Image */}
                    <div className="relative aspect-square bg-black">
                      {product.image_url ? (
                        <img 
                          src={product.image_url} 
                          alt={product.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#FFE500]/20 to-[#E91E8C]/20">
                          <Package className="w-16 h-16 text-white/50" />
                        </div>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="p-4">
                      <h3 className="font-bold text-white mb-2">
                        {product.title}
                      </h3>
                      {product.description && (
                        <p className="text-gray-400 text-sm mb-3 line-clamp-2">{product.description}</p>
                      )}
                      {/* Result/Impact */}
                      {product.result && (
                        <div className="mt-3 px-3 py-2 bg-[#FFE500]/10 border border-[#FFE500]/30 rounded-lg">
                          <p className="text-[#FFE500] text-sm font-bold flex items-center gap-1">
                            <Star className="w-4 h-4" />
                            {product.result}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ===== CTA SECTION - Black with opacity ===== */}
      <section className="relative py-20">
        <div className="absolute inset-0 bg-black/70" />
        
        <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <h2 
            className="text-3xl md:text-4xl font-bold mb-6"
            style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
          >
            <span className="text-[#FFE500]">¿LISTO PARA</span>{" "}
            <span className="text-white">LICENCIAR?</span>
          </h2>
          
          <p className="text-xl text-gray-300 mb-8">
            Hablemos sobre cómo llevar a Darkar y sus amigos a tus productos.
          </p>
          
          <Link to="/contacto">
            <Button 
              className="bg-white hover:bg-gray-100 text-black text-lg px-10 py-6 font-black hand-drawn-subtle border-[3px] border-black"
              style={{ fontFamily: "'Supersonic Rocketship', Impact, sans-serif" }}
              data-testid="cta-contact"
            >
              <Mail className="mr-2" />
              SOLICITAR LICENCIA
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <div className="mt-auto">
        <Footer />
      </div>
    </div>
  );
}
